/**
 * Runtime configuration helper
 *
 * This module provides access to environment variables that can be configured at runtime.
 *
 * In production (Docker):
 * - Variables come from window.ENV, which is generated by entrypoint.sh from container environment variables
 * - This allows the same Docker image to run in different environments (dev, QA, prod)
 *
 * In development (npm run dev):
 * - Variables come from import.meta.env, which is populated from .env files
 * - window.ENV exists but has undefined values, so we fall back to import.meta.env
 *
 * In Ladle (component development):
 * - Same as development, but auth/API config may not be set
 * - Missing config will log a warning but won't throw
 */

// Extend Window interface to include runtime config
declare global {
  interface Window {
    ENV?: {
      VITE_FUSIONAUTH_URL?: string
      VITE_FUSIONAUTH_CLIENT_ID?: string
      VITE_API_URL?: string
      VITE_GOOGLE_MAPS_API_KEY?: string
      VITE_SMARTY_EMBEDDED_KEY?: string
      VITE_GOOGLE_CLIENT_ID?: string
      VITE_SENTRY_DSN?: string
      VITE_SENTRY_ENVIRONMENT?: string
      VITE_HEAP_APP_ID?: string
    }
  }
}

// Track which warnings we've already logged to avoid spam
const warnedKeys = new Set<string>()

/**
 * Get a configuration value, preferring runtime config over build-time config.
 * Logs a warning if the value is missing (once per key).
 */
function getConfig(key: keyof NonNullable<Window['ENV']>): string {
  // In production: window.ENV is populated by entrypoint.sh
  // In development: window.ENV exists but values are undefined, so fall back to import.meta.env
  const value = window.ENV?.[key] || import.meta.env[key]

  if (!value && !warnedKeys.has(key)) {
    warnedKeys.add(key)
    console.warn(`[config] ${key} is not set. Some features may not work correctly.`)
  }

  return value || ''
}

/**
 * Get an optional configuration value (no warning if missing).
 * Used for optional services like Sentry.
 */
function getOptionalConfig(key: keyof NonNullable<Window['ENV']>): string {
  return window.ENV?.[key] || import.meta.env[key] || ''
}

// Export configuration values
export const config = {
  fusionAuthUrl: getConfig('VITE_FUSIONAUTH_URL'),
  fusionAuthClientId: getConfig('VITE_FUSIONAUTH_CLIENT_ID'),
  apiUrl: getConfig('VITE_API_URL'),
  googleMapsApiKey: getConfig('VITE_GOOGLE_MAPS_API_KEY'),
  smartyEmbeddedKey: getConfig('VITE_SMARTY_EMBEDDED_KEY'),
  googleClientId: getConfig('VITE_GOOGLE_CLIENT_ID'),
  // Optional monitoring config (no warning if not set)
  sentryDsn: getOptionalConfig('VITE_SENTRY_DSN'),
  sentryEnvironment: getOptionalConfig('VITE_SENTRY_ENVIRONMENT'),
  // Optional analytics config (no warning if not set). This gates the dormant
  // Heap loader; it is intentionally unset in every environment so the loader
  // emits nothing until explicitly enabled.
  heapAppId: getOptionalConfig('VITE_HEAP_APP_ID') || undefined,
}

export default config

